import { useEffect, useMemo, useState } from "react";
import { Minus, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const PREORDER_MESSAGE =
  "All party platter orders must be placed at least one day in advance. Same-day orders are not accepted.";

const slug = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const quantitiesToLines = (quantities) =>
  Object.entries(quantities)
    .filter(([, quantity]) => quantity > 0)
    .map(([name, quantity]) => ({ name, quantity }));

export function PartyPlatterCustomizer({
  item,
  open,
  onClose,
  onSave,
  initialPlatter = null,
  submitLabel = "Add to cart",
}) {
  const config = item?.platter_config;
  const [included, setIncluded] = useState({});
  const [extras, setExtras] = useState({});
  const [nigiri, setNigiri] = useState("");
  const [sashimi, setSashimi] = useState("");

  useEffect(() => {
    if (!item || !config) {
      return;
    }
    const restore = (lines) => Object.fromEntries(
      config.roll_options.map((roll) => [
        roll,
        lines?.find((line) => line.name === roll)?.quantity || 0,
      ])
    );
    setIncluded(restore(initialPlatter?.included_rolls));
    setExtras(restore(initialPlatter?.extra_rolls));
    setNigiri(initialPlatter?.nigiri || "");
    setSashimi(initialPlatter?.sashimi || "");
  }, [item, config, initialPlatter]);

  const includedCount = useMemo(
    () => Object.values(included).reduce((sum, quantity) => sum + quantity, 0),
    [included]
  );
  const extraCount = useMemo(
    () => Object.values(extras).reduce((sum, quantity) => sum + quantity, 0),
    [extras]
  );
  const extraTotal = useMemo(
    () => Math.round(extraCount * config?.extra_roll_price * 100) / 100,
    [config?.extra_roll_price, extraCount]
  );
  const total = useMemo(
    () => Math.round(((item?.price || 0) + extraTotal) * 100) / 100,
    [item?.price, extraTotal]
  );

  if (!item || !config) {
    return null;
  }

  const updateIncluded = (name, change) => {
    setIncluded((current) => {
      const currentValue = current[name] || 0;
      const nextValue = Math.max(0, currentValue + change);
      const nextCount = includedCount - currentValue + nextValue;
      if (change > 0 && nextCount > config.required_rolls) {
        return current;
      }
      return { ...current, [name]: nextValue };
    });
  };

  const updateExtras = (name, change) => {
    setExtras((current) => ({
      ...current,
      [name]: Math.max(0, (current[name] || 0) + change),
    }));
  };

  const requirementsMet =
    includedCount === config.required_rolls &&
    (!config.nigiri_options?.length || !!nigiri) &&
    (!config.sashimi_options?.length || !!sashimi);
  const extraPieces = extraCount * config.pieces_per_roll;
  const totalPieces = config.base_pieces + extraPieces;

  const save = () => {
    if (!requirementsMet) {
      return;
    }
    onSave({
      included_rolls: quantitiesToLines(included),
      extra_rolls: quantitiesToLines(extras),
      nigiri,
      sashimi,
      extra_total: extraTotal,
      total_pieces: totalPieces,
      config,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="flex h-[100dvh] max-w-5xl flex-col overflow-hidden rounded-none p-0 sm:h-[92vh] sm:rounded-2xl"
        data-testid="platter-customizer-dialog"
        hideClose
      >
        <div className="flex items-start gap-4 border-b border-neutral-200 bg-white p-4 sm:p-6">
          <img
            src={item.image_url}
            alt={item.name}
            className="h-20 w-28 rounded-lg object-cover sm:h-24 sm:w-36"
            data-testid="platter-customizer-image"
          />
          <div className="min-w-0 flex-1">
            <p className="eyebrow">Party platter</p>
            <h2 className="font-display text-2xl font-bold text-neutral-950" data-testid="platter-customizer-title">
              {item.name}
            </h2>
            <p className="mt-1 text-sm text-neutral-600" data-testid="platter-customizer-description">
              {item.description}
            </p>
            <p className="mt-2 font-display text-lg font-bold text-[hsl(var(--primary))]">
              ${item.price.toFixed(2)} · {config.base_pieces} pieces
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-neutral-500 transition-colors hover:bg-neutral-100"
            aria-label="Close platter customizer"
            data-testid="platter-customizer-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-neutral-50 px-4 py-5 sm:px-6">
          <div className="mx-auto max-w-3xl space-y-5 pb-28">
            <div
              className="border-l-4 border-[hsl(var(--primary))] bg-orange-50 p-4 text-sm text-neutral-700"
              data-testid="platter-preorder-notice"
            >
              <p className="font-semibold text-neutral-950">PARTY PLATTER PREORDER NOTICE</p>
              <p className="mt-1">{PREORDER_MESSAGE}</p>
              <a
                href="tel:7182162013"
                className="mt-2 inline-block font-semibold text-[hsl(var(--primary))] underline"
                data-testid="platter-contact-phone"
              >
                Questions or special requests? Call 718-216-2013.
              </a>
            </div>

            <ChoiceSection
              title="Choose your sushi rolls"
              description={`Included rolls: ${includedCount} / ${config.required_rolls}`}
              testId="platter-included-section"
            >
              {config.roll_options.map((roll) => (
                <RollQuantityRow
                  key={`included-${roll}`}
                  name={roll}
                  quantity={included[roll] || 0}
                  onDecrease={() => updateIncluded(roll, -1)}
                  onIncrease={() => updateIncluded(roll, 1)}
                  onToggle={() => setIncluded((current) => ({
                    ...current,
                    [roll]: current[roll] ? 0 : includedCount < config.required_rolls ? 1 : 0,
                  }))}
                  increaseDisabled={includedCount >= config.required_rolls}
                  testPrefix="included-roll"
                />
              ))}
            </ChoiceSection>

            {config.nigiri_options?.length > 0 && (
              <RadioSection
                title="Choose your nigiri"
                description="Includes 8 pieces."
                options={config.nigiri_options}
                value={nigiri}
                onChange={setNigiri}
                testPrefix="platter-nigiri"
              />
            )}

            {config.sashimi_options?.length > 0 && (
              <RadioSection
                title="Choose your sashimi"
                description="Includes 8 pieces."
                options={config.sashimi_options}
                value={sashimi}
                onChange={setSashimi}
                testPrefix="platter-sashimi"
              />
            )}

            <ChoiceSection
              title="Add extra sushi rolls"
              description={`$${config.extra_roll_price.toFixed(2)} per additional roll · Extras: $${extraTotal.toFixed(2)}`}
              testId="platter-extras-section"
            >
              {config.roll_options.map((roll) => (
                <RollQuantityRow
                  key={`extra-${roll}`}
                  name={roll}
                  quantity={extras[roll] || 0}
                  onDecrease={() => updateExtras(roll, -1)}
                  onIncrease={() => updateExtras(roll, 1)}
                  testPrefix="extra-roll"
                  price={config.extra_roll_price}
                />
              ))}
            </ChoiceSection>

            <section className="border border-neutral-200 bg-white p-5" data-testid="platter-order-summary">
              <h3 className="font-display text-lg font-bold">Order summary</h3>
              <p className="mt-2 text-sm text-neutral-600">{item.name}</p>
              <SummaryLines label="Included rolls" lines={quantitiesToLines(included)} />
              {nigiri && <p className="mt-2 text-sm">Nigiri: {nigiri} (8 pieces)</p>}
              {sashimi && <p className="mt-2 text-sm">Sashimi: {sashimi} (8 pieces)</p>}
              <SummaryLines label="Extra rolls" lines={quantitiesToLines(extras)} />
              <div className="mt-4 flex justify-between border-t border-neutral-200 pt-3 text-sm text-neutral-600">
                <span>Total pieces</span>
                <span data-testid="platter-total-pieces">{totalPieces}</span>
              </div>
              <div className="mt-2 flex justify-between font-display text-xl font-bold">
                <span>Total</span>
                <span data-testid="platter-final-price">${total.toFixed(2)}</span>
              </div>
            </section>
          </div>
        </div>

        <div className="border-t border-neutral-200 bg-white p-4 sm:px-6">
          {!requirementsMet && (
            <p className="mb-2 text-center text-xs text-neutral-500" data-testid="platter-requirements-message">
              Complete all required roll and seafood selections to add this platter.
            </p>
          )}
          <Button
            type="button"
            onClick={save}
            disabled={!requirementsMet}
            className="h-12 w-full rounded-xl bg-neutral-900 text-base hover:bg-[hsl(var(--primary))]"
            data-testid="platter-add-to-cart"
          >
            {submitLabel} — ${total.toFixed(2)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChoiceSection({ title, description, testId, children }) {
  return (
    <section className="border border-neutral-200 bg-white p-5" data-testid={testId}>
      <h3 className="font-display text-lg font-bold">{title}</h3>
      <p className="mt-1 text-sm text-neutral-500">{description}</p>
      <div className="mt-4 divide-y divide-neutral-100">{children}</div>
    </section>
  );
}

function RollQuantityRow({
  name,
  quantity,
  onDecrease,
  onIncrease,
  onToggle,
  increaseDisabled,
  testPrefix,
  price,
}) {
  const id = slug(name);
  return (
    <div className="flex min-h-12 items-center gap-3 py-2" data-testid={`${testPrefix}-row-${id}`}>
      <input
        type="checkbox"
        checked={quantity > 0}
        onChange={onToggle}
        disabled={quantity === 0 && increaseDisabled}
        className="h-4 w-4 accent-[hsl(var(--primary))]"
        aria-label={`Select ${name}`}
        data-testid={`${testPrefix}-checkbox-${id}`}
      />
      <span className="min-w-0 flex-1 text-sm font-medium text-neutral-800">{name}</span>
      {price != null && <span className="text-xs text-neutral-500">${price.toFixed(2)}</span>}
      <div className="flex items-center border border-neutral-200" data-testid={`${testPrefix}-quantity-${id}`}>
        <button
          type="button"
          onClick={onDecrease}
          disabled={quantity === 0}
          className="p-2 text-neutral-600 disabled:opacity-30"
          aria-label={`Remove one ${name}`}
          data-testid={`${testPrefix}-decrease-${id}`}
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <span className="w-7 text-center text-sm font-semibold" data-testid={`${testPrefix}-count-${id}`}>
          {quantity}
        </span>
        <button
          type="button"
          onClick={onIncrease}
          disabled={increaseDisabled}
          className="p-2 text-neutral-900 disabled:opacity-30"
          aria-label={`Add one ${name}`}
          data-testid={`${testPrefix}-increase-${id}`}
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function RadioSection({ title, description, options, value, onChange, testPrefix }) {
  return (
    <section className="border border-neutral-200 bg-white p-5" data-testid={`${testPrefix}-section`}>
      <h3 className="font-display text-lg font-bold">{title}</h3>
      <p className="mt-1 text-sm text-neutral-500">{description}</p>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {options.map((option) => {
          const id = slug(option);
          return (
            <label
              key={option}
              className="flex cursor-pointer items-center gap-3 border border-neutral-200 p-3 text-sm"
              data-testid={`${testPrefix}-option-${id}`}
            >
              <input
                type="radio"
                name={testPrefix}
                value={option}
                checked={value === option}
                onChange={() => onChange(option)}
                className="accent-[hsl(var(--primary))]"
                data-testid={`${testPrefix}-radio-${id}`}
              />
              <span className="font-medium">{option}</span>
            </label>
          );
        })}
      </div>
    </section>
  );
}

function SummaryLines({ label, lines }) {
  if (lines.length === 0) {
    return null;
  }
  return (
    <div className="mt-3 text-sm text-neutral-600">
      <p className="font-semibold text-neutral-800">{label}</p>
      {lines.map((line) => (
        <p key={line.name}>{line.name} × {line.quantity}</p>
      ))}
    </div>
  );
}